import { useEffect, useState, useCallback } from 'react';

/**
 * Tracks the last article the user opened, so we can offer a
 * "Continue reading where you left off" CTA on the Home page.
 *
 * Persisted to localStorage under `ip_last_article_v1`.
 *
 * Schema :
 * {
 *   slug: 'friendly-nations-visa-panama',
 *   category: 'visa-residence',
 *   lang: 'fr',
 *   title: 'Le visa Friendly Nations en 2026 …',
 *   excerpt: '…',
 *   featuredImage: 'https://…',
 *   visitedAt: '2026-05-13T18:30:00Z',
 *   scrollPercent: 42       // optional, set by the article reader
 * }
 *
 * Auto-expires after 30 days (we don't want to surface a stale article).
 */

const STORAGE_KEY = 'ip_last_article_v1';
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function loadState() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.visitedAt) return null;
    const age = Date.now() - Date.parse(parsed.visitedAt);
    if (age > EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveState(state) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // private mode / quota — silent fail
  }
}

export function useLastArticle() {
  const [last, setLast] = useState(loadState);

  // Refresh when another tab updates (rare, but cheap)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setLast(loadState());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const record = useCallback((article) => {
    if (!article?.slug || !article?.category || !article?.lang) return;
    const entry = {
      slug: article.slug,
      category: article.category,
      lang: article.lang,
      title: article.title || '',
      excerpt: article.excerpt || '',
      featuredImage: article.featuredImage || null,
      visitedAt: new Date().toISOString(),
      scrollPercent: 0,
    };
    saveState(entry);
    setLast(entry);
  }, []);

  const updateScrollPercent = useCallback((percent) => {
    setLast((prev) => {
      if (!prev) return prev;
      // Throttle : only update if change > 5 %
      if (Math.abs((prev.scrollPercent || 0) - percent) < 5) return prev;
      const next = { ...prev, scrollPercent: percent };
      saveState(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(STORAGE_KEY);
    setLast(null);
  }, []);

  return { last, record, updateScrollPercent, clear };
}
